package com.banyan.flightviewer.web.components.airport

import com.banyan.flightviewer.data.AirportDatabase
import com.banyan.flightviewer.data.AtcFeedDatabase
import com.banyan.flightviewer.state.AirportInfo
import com.banyan.flightviewer.state.AtcStreamInfo
import com.banyan.flightviewer.state.MetarData
import com.banyan.flightviewer.web.components.util.formatLocalTimeAtAirport
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.button
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.dom.removeAllChildren
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlin.math.roundToInt
import org.w3c.dom.Element
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLImageElement

/**
 * General tab: photo + info row + weather + ATC feeds.
 *
 * Caches via [AirportCache] so clicking back-and-forth between airports
 * doesn't burn FR24 quota.
 *
 * Photo fetch + weather fetch + airport-info fetch run in parallel.
 * ATC feeds come from the bundled [AtcFeedDatabase] (no server round-trip).
 */
fun mountAirportGeneralTab(parent: Element, app: WebAppContainer, code: String) {
    val container = parent.div(className = "fv-airport__general")

    // ── Photo (Wikipedia-backed) ────────────────────────────────────────
    val photoEl = document.createElement("img") as HTMLImageElement
    photoEl.className = "fv-airport__photo"
    photoEl.alt = AirportDatabase.getName(code)
    photoEl.asDynamic().style.display = "none"
    photoEl.addEventListener("load", { photoEl.asDynamic().style.display = "block" })
    container.appendChild(photoEl)
    app.appState.scope.launch {
        val url = app.serverClient.getAirportPhotoUrl(code)
        if (url != null && app.appState.selectedAirport.value == code) {
            photoEl.src = url
        }
    }

    // ── Info row (city/country/elevation/timezone/local time/coords) ────
    // Mirrors desktop GeneralTab (AirportInfoPanel.kt:307-333).
    val infoRow = container.div(className = "fv-airport__info-row")
    infoRow.asDynamic().style.display = "none"    // hidden until the info fetch resolves
    app.appState.scope.launch {
        val cached = AirportCache.getInfo(code)
        val info = if (cached != null) cached
        else app.serverClient.getAirportInfo(code).also { AirportCache.putInfo(code, it) }
        if (info != null && app.appState.selectedAirport.value == code) {
            renderInfoRow(infoRow, info)
            infoRow.asDynamic().style.display = "grid"
        }
    }

    // ── Weather (fetched via METAR proxy) ───────────────────────────────
    val weatherHost = container.div(className = "fv-airport__weather") {
        textContent = "Loading weather\u2026"
    }
    app.appState.scope.launch {
        val metar = app.serverClient.getMetar(code)
        if (app.appState.selectedAirport.value == code) {
            renderWeather(weatherHost, metar)
        }
    }

    // ── ATC feeds (bundled database, not server) ────────────────────────
    // Feeds arrive in two logical groups: special-event feeds (non-blank
    // `category`, e.g. Sun 'n Fun) followed by regular feeds. We render
    // each non-blank category as its own titled section above the regular
    // list so users can see at a glance which feeds are event-only.
    val feeds = AtcFeedDatabase.feedsFor(code)
    if (feeds.isEmpty()) {
        container.div(className = "fv-airport__empty") {
            textContent = "No ATC feeds bundled for this airport."
        }
    } else {
        val byCategory = feeds.groupBy { it.category }
        // Preserve insertion order via distinct().filterNotNull() on the
        // raw feed list: event categories (non-blank) come first per the
        // AtcFeedDatabase ordering, then the regular "" group.
        val orderedCategories = feeds.map { it.category }.distinct()
        orderedCategories.forEach { cat ->
            val group = byCategory[cat] ?: return@forEach
            if (cat.isNotBlank()) {
                container.div(className = "fv-airport__feeds-section") {
                    textContent = cat
                }
            }
            container.div(className = "fv-airport__feeds") {
                group.forEach { feed -> mountFeedRow(this, app, feed) }
            }
        }
    }
}

// ── Info row rendering ────────────────────────────────────────────────

private fun renderInfoRow(host: Element, info: AirportInfo) {
    host.removeAllChildren()
    val location = listOf(info.city, info.country).filter { it.isNotBlank() }.joinToString(", ")
    if (location.isNotBlank()) {
        infoCell(host, "LOCATION", location, fullWidth = true)
    }
    val localTime = formatLocalTimeAtAirport(info.timezone)
    if (localTime.isNotBlank() || info.timezone.isNotBlank()) {
        infoCell(host, "LOCAL TIME", localTime.ifBlank { "\u2014" })
        val tzLabel = if (info.utcOffset.isNotBlank()) "${info.timezone} (${info.utcOffset})"
        else info.timezone.ifBlank { "\u2014" }
        infoCell(host, "TIMEZONE", tzLabel)
    }
    if (info.elevation != 0 || info.lat != 0.0 || info.lon != 0.0) {
        infoCell(host, "ELEVATION", "${info.elevation} ft")
        val coords = "${formatDegrees(info.lat)}, ${formatDegrees(info.lon)}"
        infoCell(host, "COORDINATES", coords)
    }
}

private fun infoCell(parent: Element, label: String, value: String, fullWidth: Boolean = false) {
    val cell = parent.div(className = if (fullWidth) "fv-airport__info-cell fv-airport__info-cell--wide"
    else "fv-airport__info-cell")
    cell.div(className = "fv-airport__info-label") { textContent = label }
    cell.div(className = "fv-airport__info-value") { textContent = value }
}

private fun formatDegrees(v: Double): String {
    val rounded = (v * 10000).toLong() / 10000.0
    return rounded.toString()
}

// ── Weather rendering (moved from AirportPanel.kt) ────────────────────

private fun renderWeather(host: HTMLDivElement, metar: MetarData?) {
    host.removeAllChildren()
    host.className = "fv-airport__weather"

    if (metar == null || (metar.raw.isBlank() && metar.temp == null && metar.windSpeed == null)) {
        host.div(className = "fv-airport__weather-empty") {
            textContent = "Weather data unavailable"
        }
        return
    }

    host.div(className = "fv-airport__weather-head") {
        div(className = "fv-airport__weather-cond") {
            div(className = "fv-airport__weather-icon") {
                textContent = weatherIcon(metar.condition, isNight = false)
            }
            div(className = "fv-airport__weather-condname") {
                textContent = metar.condition.ifBlank { "\u2014" }
            }
        }

        div(className = "fv-airport__weather-col") {
            div(className = "fv-airport__weather-label") { textContent = "TEMP" }
            div(className = "fv-airport__weather-value") {
                textContent = metar.temp?.let { "${it}\u00B0C" } ?: "\u2014"
            }
            if (metar.temp != null) {
                div(className = "fv-airport__weather-sub") {
                    textContent = "${metar.temp!! * 9 / 5 + 32}\u00B0F"
                }
            }
        }

        div(className = "fv-airport__weather-col") {
            div(className = "fv-airport__weather-label") { textContent = "WIND" }
            div(className = "fv-airport__weather-value") { textContent = windText(metar) }
        }
    }

    host.div(className = "fv-airport__weather-grid") {
        weatherCell(this, "VISIBILITY", metar.visibility ?: "\u2014")
        weatherCell(this, "CLOUDS", metar.clouds.ifBlank { "Clear" })
        val altHpa = metar.altimeter
        val altText = if (altHpa != null) {
            val inHg = (altHpa * 0.02953 * 100).roundToInt() / 100.0
            "${altHpa.roundToInt()} hPa \u00B7 $inHg\"Hg"
        } else "\u2014"
        weatherCell(this, "ALTIMETER", altText)
    }

    if (metar.raw.isNotBlank()) {
        host.div(className = "fv-airport__weather-raw") { textContent = metar.raw }
    }
}

private fun weatherCell(parent: Element, label: String, value: String) {
    parent.div {
        div(className = "fv-airport__weather-label") { textContent = label }
        div(className = "fv-airport__weather-value fv-airport__weather-value--sm") {
            textContent = value
        }
    }
}

private fun windText(metar: MetarData): String {
    val speed = metar.windSpeed ?: return "Calm"
    if (speed == 0) return "Calm"
    val dir = metar.windDir?.let { "${it}\u00B0" } ?: "VRB"
    val gust = metar.windGust?.let { "G$it" } ?: ""
    return "$dir $speed$gust kts"
}

private fun weatherIcon(condition: String, isNight: Boolean): String = when {
    condition.contains("Thunder", ignoreCase = true) -> "\u26C8\uFE0F"
    condition.contains("Rain", ignoreCase = true) -> "\uD83C\uDF27\uFE0F"
    condition.contains("Snow", ignoreCase = true) -> "\u2744\uFE0F"
    condition.contains("Fog", ignoreCase = true) || condition.contains("Mist", ignoreCase = true) ->
        "\uD83C\uDF2B\uFE0F"
    condition.contains("Overcast", ignoreCase = true) -> "\u2601\uFE0F"
    condition.contains("cloudy", ignoreCase = true) ->
        if (isNight) "\u2601\uFE0F" else "\u26C5"
    condition.contains("Few", ignoreCase = true) ->
        if (isNight) "\uD83C\uDF19" else "\uD83C\uDF24\uFE0F"
    else -> if (isNight) "\uD83C\uDF19" else "\u2600\uFE0F"
}

// ── ATC feed row (moved from AirportPanel.kt) ─────────────────────────

private fun mountFeedRow(parent: Element, app: WebAppContainer, feed: AtcStreamInfo) {
    val row = parent.div(className = "fv-airport__feed")

    val titleSpan = document.createElement("span")
    titleSpan.textContent = feed.feedName
    row.appendChild(titleSpan)

    val btn = row.button(className = "fv-btn fv-btn--ghost") {
        textContent = "Play"
    }

    btn.onClick {
        if (app.appState.atcStream.value?.url == feed.url) {
            app.audioPlayer.stop()
        } else {
            app.audioPlayer.play(feed)
        }
    }

    app.appState.scope.bindFlow(app.appState.atcStream) { stream ->
        val playing = stream?.url == feed.url
        row.className = if (playing) "fv-airport__feed playing" else "fv-airport__feed"
        btn.textContent = if (playing) "Stop" else "Play"
    }
}
